'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useUser, SignedIn, UserButton } from '@clerk/nextjs';

const navigationItems = [
  { label: 'Upload', href: '/document-upload', icon: 'DocumentArrowUpIcon' },
  { label: 'Dashboard', href: '/offer-analysis-dashboard', icon: 'ChartBarIcon' },
  { label: 'Checklist', href: '/interactive-checklist', icon: 'ClipboardDocumentCheckIcon' },
  { label: 'Guidance', href: '/negotiation-guidance', icon: 'LightBulbIcon' },
  { label: 'Export', href: '/results-export', icon: 'ArrowDownTrayIcon' },
];

export default function Header() {
  const { user } = useUser();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  const isActive = (href: string) => pathname === href;

  useEffect(() => {
    if (user) {
      fetchCredits();
    } else {
      setCreditsLoading(false);
    }
  }, [user]);

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await fetch('/api/user/credits');
      const data = await response.json();
      if (response.ok) {
        setCredits(data.credits);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Expose refresh function for other components
  useEffect(() => {
    (window as any).refreshCredits = fetchCredits;
    return () => {
      delete (window as any).refreshCredits;
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link href="/offer-analysis-dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
              <Icon name="DocumentTextIcon" size={24} className="text-primary-foreground" variant="solid" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-text-secondary leading-tight">Offer</span>
              <span className="text-xl font-bold text-primary leading-tight">XRay</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name={item.icon as any} size={18} variant={isActive(item.href) ? 'solid' : 'outline'} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <SignedIn>
              {/* Credits Display */}
              <Link
                href="/pricing"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 ${
                  credits === 0
                    ? 'bg-error/10 border border-error/30 hover:bg-error/20'
                    : 'bg-primary/10 border border-primary/30 hover:bg-primary/20'
                }`}
              >
                {creditsLoading ? (
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon 
                      name="CurrencyDollarIcon" 
                      size={18} 
                      className={credits === 0 ? 'text-error' : 'text-primary'} 
                    />
                    <span className={`text-sm font-semibold ${credits === 0 ? 'text-error' : 'text-primary'}`}>
                      {credits ?? 0} credits
                    </span>
                    {credits === 0 && (
                      <Icon name="PlusIcon" size={14} className="text-error" />
                    )}
                  </>
                )}
              </Link>

              <div className="flex items-center gap-3">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium text-text-primary">
                    {user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')?.[0] || 'User'}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {user?.emailAddresses?.[0]?.emailAddress || ''}
                  </p>
                </div>
                <UserButton 
                  afterSignOutUrl="/login"
                  appearance={{
                    elements: {
                      avatarBox: 'w-10 h-10',
                    },
                  }}
                />
              </div>
            </SignedIn>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-surface">
          <nav className="flex flex-col p-4 gap-2">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name={item.icon as any} size={20} variant={isActive(item.href) ? 'solid' : 'outline'} />
                <span>{item.label}</span>
              </Link>
            ))}
            {/* Mobile Credits Link */}
            <Link
              href="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                credits === 0
                  ? 'bg-error/10 text-error'
                  : 'bg-primary/10 text-primary'
              }`}
            >
              {creditsLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span>Loading credits...</span>
                </>
              ) : (
                <>
                  <Icon name="CurrencyDollarIcon" size={20} />
                  <span>{credits ?? 0} credits</span>
                  {credits === 0 && <span className="text-xs">(Buy more)</span>}
                </>
              )}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
